import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { callApi, HttpMethod } from "src/services/apiService";
import { toast } from "sonner";
import Loading from "src/components/Loading";
import {
  Grid,
  GridColumn as Column,
  GridCellProps,
  GridSortChangeEvent,
  GridFilterChangeEvent,
} from "@progress/kendo-react-grid";
import { SortDescriptor, orderBy, CompositeFilterDescriptor, filterBy } from "@progress/kendo-data-query";
import GoHighLevelNav from "./GoHighLevelNav";
import styles from "./GoHighLevel.module.css";

interface Agency {
  companyId: string;
  agencyName: string;
  isActive: boolean;
  isInstalled: boolean;
  hasDncCredentials: boolean;
  installedAt: string;
  subAccountCount: number;
  totalContacts: number;
  scrubLimit: number | null;
}

const YesNoCell = (field: keyof Agency) => (props: GridCellProps) => {
  const on = Boolean(props.dataItem[field]);
  return (
    <td style={{ textAlign: "center" }}>
      <span className={`badge ${on ? "bg-success" : "bg-secondary"}`}>{on ? "Yes" : "No"}</span>
    </td>
  );
};

const Agencies: React.FC = () => {
  const navigate = useNavigate();
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState<SortDescriptor[]>([{ field: "agencyName", dir: "asc" }]);
  const [gridFilter, setGridFilter] = useState<CompositeFilterDescriptor | undefined>(undefined);

  useEffect(() => {
    (async () => {
      try {
        setAgencies(await callApi("sysadmin/gohighlevel/agencies", HttpMethod.GET));
      } catch {
        toast.error("Failed to load agencies.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const data = useMemo(
    () => (gridFilter ? filterBy(orderBy(agencies, sort), gridFilter) : orderBy(agencies, sort)),
    [agencies, sort, gridFilter],
  );

  const NumberCell = (field: keyof Agency) => (props: GridCellProps) => (
    <td style={{ textAlign: "right" }}>{Number(props.dataItem[field] ?? 0).toLocaleString()}</td>
  );

  const formatMdy = (iso: string) => {
    const d = new Date(iso);
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${mm}/${dd}/${d.getFullYear()}`;
  };

  const DateCell = (props: GridCellProps) => {
    const v = props.dataItem.installedAt;
    return <td style={{ textAlign: "center" }}>{v ? formatMdy(v) : ""}</td>;
  };

  const ScrubLimitCell = (props: GridCellProps) => {
    const v = props.dataItem.scrubLimit;
    return <td style={{ textAlign: "right" }}>{v != null ? Number(v).toLocaleString() : "—"}</td>;
  };

  const ActionCell = (props: GridCellProps) => (
    <td>
      <button
        className={styles.linkButton}
        onClick={() => navigate(`/sysadmin/gohighlevel/agencies/${props.dataItem.companyId}`)}
      >
        View Sub-Accounts
      </button>
    </td>
  );

  if (loading) return <Loading />;

  return (
    <div className={styles.container}>
      <GoHighLevelNav />
      <div className={styles.pageHeader}>
        <div>
          <h2 className={styles.pageTitle}>Agencies</h2>
          <p className={styles.pageSubtitle}>{agencies.length} connected agencies</p>
        </div>
      </div>

      <Grid
        className="sysadmin-grid"
        data={data}
        dataItemKey="companyId"
        sortable={true}
        resizable={true}
        sort={sort}
        onSortChange={(e: GridSortChangeEvent) => setSort(e.sort)}
        filterable={true}
        filter={gridFilter}
        onFilterChange={(e: GridFilterChangeEvent) => setGridFilter(e.filter)}
        style={{ height: "calc(100vh - 190px)", width: "100%" }}
      >
        <Column field="agencyName" title="Agency" width={280} />
        <Column field="isInstalled" title="Installed" width={120} filterable={false} cells={{ data: YesNoCell("isInstalled") }} />
        <Column field="isActive" title="Active" width={110} filterable={false} cells={{ data: YesNoCell("isActive") }} />
        <Column field="hasDncCredentials" title="DNC Creds" width={130} filterable={false} cells={{ data: YesNoCell("hasDncCredentials") }} />
        <Column field="subAccountCount" title="Sub-accounts" width={150} filterable={false} cells={{ data: NumberCell("subAccountCount") }} />
        <Column field="totalContacts" title="Total Contacts" width={160} filterable={false} cells={{ data: NumberCell("totalContacts") }} />
        <Column field="scrubLimit" title="Scrubs Included in Plan" width={150} filterable={false} cells={{ data: ScrubLimitCell }} />
        <Column field="installedAt" title="Installed" width={140} filterable={false} cells={{ data: DateCell }} />
        <Column title="Actions" width={170} filterable={false} sortable={false} cells={{ data: ActionCell }} />
      </Grid>
    </div>
  );
};

export default Agencies;
